import {
  Alert,
  Button,
  Descriptions,
  Input,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { CheckCircleOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import PageTitle from 'components/page-title';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import {
  CREATOR_BRANCH_LABEL,
  MILESTONE_STATUS_COLOR,
  MILESTONE_STATUS_LABEL,
  MILESTONE_WAITING_CAP_LABEL,
  RECRUIT_MILESTONE_CODES,
  RECRUIT_MILESTONE_LABEL,
  RECRUIT_STATUS_COLOR,
  RECRUIT_STATUS_LABEL,
  RECRUIT_STATUS_OPTIONS,
  labelOf,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import {
  RecruitMilestone,
  RecruitMilestoneCode,
  RecruitStatus,
  RecruitView,
} from 'pages/star-shared/types';
import {
  TABLE_EMPTY,
  formatDate,
  formatDateTime,
  formatNumber,
  personName,
} from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import { useStarRules } from 'pages/star-shared/use-star-rules';
import StarAmount from 'pages/star-shared/components/star-amount';
import { NoWrap, UserCell } from 'pages/star-shared/components/status-tags';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { PickedValue, StarAccountSelect } from 'pages/star-shared/components/remote-search-select';
import { colors } from 'theme/tokens';
import RecruitCreateModal from './components/recruit-create-modal';
import ConfirmMilestoneModal, {
  confirmState,
  milestoneDeadline,
} from './components/confirm-milestone-modal';

const LIMIT = 20;

// "Hết ngày" của khoảng nửa mở [start, end): ngày trước `end`.
const lastDayOf = (end?: string | null) =>
  end ? formatDate(dayjs(end).subtract(1, 'day').format('YYYY-MM-DD')) : '—';

function deadlineText(r: RecruitView, code: RecruitMilestoneCode): string {
  const end = milestoneDeadline(r, code);
  if (!end) return '';
  return code === 'RETAIN' ? `Xét ngày ${formatDate(end)}` : `Hạn: hết ${lastDayOf(end)}`;
}

const MilestoneCell = ({ r, code }: { r: RecruitView; code: RecruitMilestoneCode }) => {
  const m: RecruitMilestone | undefined = r.milestones?.find((x) => x.code === code);
  if (!m) return <span>—</span>;
  const waiting = m.status === 'ACHIEVED' && !!m.waitingCap;
  const tip: ReactNode[] = [deadlineText(r, code)];
  if (m.achievedAt) tip.push(`Đạt lúc ${formatDateTime(m.achievedAt)}`);
  if (m.paidAt) tip.push(`Trả lúc ${formatDateTime(m.paidAt)}`);
  if (waiting) tip.push('Người tuyển đã chạm trần Sao tháng này — trả vào tháng sau.');
  if (m.manual) {
    tip.push(`Xác nhận tay bởi ${personName(m.confirmedBy)}${m.note ? `: ${m.note}` : ''}`);
  }
  return (
    <Tooltip
      title={
        <div>
          {tip.filter(Boolean).map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span
          style={{
            width: 70,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          {RECRUIT_MILESTONE_LABEL[code]}
        </span>
        <span style={{ width: 44, flexShrink: 0 }}>
          <StarAmount value={m.star} strong={m.status === 'PAID'} />
        </span>
        <Tag
          color={waiting ? 'gold' : MILESTONE_STATUS_COLOR[m.status]}
          style={{ marginInlineEnd: 0 }}
        >
          {waiting ? MILESTONE_WAITING_CAP_LABEL : labelOf(MILESTONE_STATUS_LABEL, m.status)}
        </Tag>
        {m.manual && (
          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
            Tay
          </Tag>
        )}
      </div>
    </Tooltip>
  );
};

const ProgressLine = ({
  label,
  value,
  target,
}: {
  label: string;
  value?: number;
  target: number;
}) => {
  const reached = typeof value === 'number' && value >= target;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ color: colors.textSecondary }}>{label}</span>
      <NoWrap>
        <strong style={{ color: reached ? colors.successInk : undefined }}>
          {typeof value === 'number' ? formatNumber(Math.round(value * 10) / 10) : '—'}
        </strong>
        /{formatNumber(target)}
      </NoWrap>
    </div>
  );
};

interface Filter {
  status?: RecruitStatus;
  recruiterId?: number;
  q?: string;
  page: number;
  limit: number;
}

// Tuyển dụng (HR) — SPEC-NHIEM-VU-MO-RONG.md §3; lời văn theo 08-chinh-sach-nhiem-vu-mo-rong.md §3.
const StarRecruitsPage = () => {
  const { userId } = useCurrentRoles();
  const { recruit: rules, fromServer } = useStarRules();
  const [filter, setFilter] = useState<Filter>({ page: 1, limit: LIMIT });
  const [recruiter, setRecruiter] = useState<PickedValue>(undefined);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<RecruitView | null>(null);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading, setItems } = usePagedList<RecruitView>(API.starsRecruits, filter, {
    refreshKey,
  });
  const refresh = () => setRefreshKey((k) => k + 1);

  // Cập nhật tại chỗ dòng vừa đổi (máy chủ trả RecruitView), không có thì tải lại.
  const applyRow = (next: RecruitView | null) => {
    if (next?.id) setItems((prev) => prev.map((r) => (r.id === next.id ? next : r)));
    else refresh();
  };

  const openCancel = (r: RecruitView) => {
    setAction({
      title: 'Huỷ phiếu tuyển dụng',
      description: (
        <div>
          <div>
            #{r.id} · Creator <strong>{personName(r.creator)}</strong> · người tuyển{' '}
            <strong>{personName(r.recruiter)}</strong>
          </div>
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message="Các mốc chưa trả sẽ dừng. Sao đã trả không thu hồi."
          />
        </div>
      ),
      noteRequired: true,
      noteLabel: 'Lý do huỷ',
      confirmText: 'Huỷ phiếu',
      danger: true,
      onSubmit: async (note) => {
        try {
          const res: any = await AxiosUtils.patch(`${API.starsRecruits}/${r.id}/cancel`, {
            reason: note,
          });
          notification.success({ message: `Đã huỷ phiếu của ${personName(r.creator)}.` });
          applyRow(res?.data && typeof res.data === 'object' ? res.data : null);
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const qualifyDone = (r: RecruitView) =>
    ['ACHIEVED', 'PAID'].includes(r.milestones?.find((m) => m.code === 'QUALIFY')?.status || '');

  // Tổng độ rộng ≈ khung bảng ở màn 1440 có thanh bên (1120px): mã phiếu xem ở dòng mở rộng,
  // 3 mốc gộp một cột (mỗi mốc một dòng) để không phải cuộn ngang.
  const columns: ColumnsType<RecruitView> = [
    {
      title: 'Creator',
      key: 'creator',
      width: 180,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <UserCell user={r.creator} />
          {r.creator?.branch && (
            <Tag color={r.creator.branch === 'DANCE' ? 'purple' : 'cyan'}>
              {labelOf(CREATOR_BRANCH_LABEL, r.creator.branch)}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Người tuyển',
      key: 'recruiter',
      width: 160,
      render: (_, r) => <UserCell user={r.recruiter} />,
    },
    {
      title: 'Bắt đầu',
      key: 'startDate',
      width: 115,
      render: (_, r) => (
        <div>
          <NoWrap>{formatDate(r.startDate)}</NoWrap>
          <div>
            <Tag color={RECRUIT_STATUS_COLOR[r.status]} style={{ marginTop: 4 }}>
              {labelOf(RECRUIT_STATUS_LABEL, r.status)}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Mốc thưởng',
      key: 'milestones',
      width: 265,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {RECRUIT_MILESTONE_CODES.map((code) => (
            <MilestoneCell key={code} r={r} code={code} />
          ))}
        </div>
      ),
    },
    {
      title: 'Tiến độ',
      key: 'stats',
      width: 170,
      render: (_, r) =>
        r.stats ? (
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            <ProgressLine
              label="Ngày live"
              value={r.stats.liveDays}
              target={rules.qualifyLiveDays}
            />
            <ProgressLine
              label="Giờ live"
              value={r.stats.liveHours}
              target={rules.qualifyLiveHours}
            />
            {qualifyDone(r) && (
              <ProgressLine
                label="Live giữ chân"
                value={r.stats.retainLiveDays}
                target={rules.retainLiveDays}
              />
            )}
            <ProgressLine label="Ngày KC" value={r.stats.kcDays} target={rules.standoutKcDays} />
            {r.stats.evaluatedAt && (
              <div style={{ color: colors.textSecondary, fontSize: 12 }}>
                Chấm {formatDateTime(r.stats.evaluatedAt)}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: colors.textSecondary }}>Chưa chấm lần nào</span>
        ),
    },
    {
      title: 'Đã trả',
      key: 'totalPaid',
      width: 86,
      align: 'right',
      render: (_, r) => <StarAmount value={r.totalPaid ?? 0} />,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 96,
      render: (_, r) => {
        const isRecruiter = !!userId && r.recruiter?.id === userId;
        const tracking = r.status === 'TRACKING';
        const canConfirm =
          tracking &&
          RECRUIT_MILESTONE_CODES.some((c) => confirmState(r, c, rules.manualGraceDays).ok);
        let confirmTip = 'Xác nhận tay một mốc';
        if (isRecruiter) confirmTip = 'Bạn là người tuyển của phiếu này — người khác xác nhận.';
        else if (!tracking) confirmTip = 'Phiếu không còn theo dõi.';
        else if (!canConfirm) confirmTip = 'Không còn mốc nào xác nhận tay được.';
        return (
          <Space size={0}>
            <Tooltip title={confirmTip}>
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                aria-label="Xác nhận tay"
                disabled={isRecruiter || !canConfirm}
                onClick={() => setConfirmTarget(r)}
              />
            </Tooltip>
            <Tooltip title={tracking ? 'Huỷ phiếu' : 'Phiếu không còn theo dõi.'}>
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                aria-label="Huỷ phiếu"
                disabled={!tracking}
                onClick={() => openCancel(r)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const expandedRow = (r: RecruitView) => (
    <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} style={{ maxWidth: 1100 }}>
      <Descriptions.Item label="Phiếu">#{r.id}</Descriptions.Item>
      <Descriptions.Item label="Hạn Đạt chuẩn">
        hết {lastDayOf(r.windows?.qualifyEnd)}
      </Descriptions.Item>
      <Descriptions.Item label="Xét Giữ chân">{formatDate(r.windows?.retainAt)}</Descriptions.Item>
      <Descriptions.Item label="Hạn Nổi bật">
        hết {lastDayOf(r.windows?.standoutEnd)}
      </Descriptions.Item>
      <Descriptions.Item label="Tạo phiếu">
        {personName(r.createdBy)} · {formatDateTime(r.createdAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Ghi chú">{r.note || '—'}</Descriptions.Item>
      {r.status === 'CANCELLED' && (
        <Descriptions.Item label="Huỷ">
          {personName(r.cancelledBy)} · {formatDateTime(r.cancelledAt)}
          {r.cancelReason ? ` — ${r.cancelReason}` : ''}
        </Descriptions.Item>
      )}
      {r.milestones
        ?.filter((m) => m.manual)
        .map((m) => (
          <Descriptions.Item
            key={m.code}
            label={`Xác nhận tay "${RECRUIT_MILESTONE_LABEL[m.code]}"`}
          >
            {personName(m.confirmedBy)}
            {m.note ? ` — ${m.note}` : ''}
          </Descriptions.Item>
        ))}
    </Descriptions>
  );

  const policy = (
    <div>
      <div style={{ marginBottom: 4 }}>
        Thưởng người tuyển được Creator Studio <strong>làm thật và ở lại</strong>. Ngày live hợp lệ
        = ngày live ≥ {rules.liveDayMinMinutes} phút theo file TikTok ngày.
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          <strong>Đạt chuẩn</strong> ({formatNumber(rules.qualifyStar)} ⭐): trong{' '}
          {rules.qualifyWindowDays} ngày đầu có ≥ {rules.qualifyLiveDays} ngày live hợp lệ và ≥{' '}
          {rules.qualifyLiveHours} giờ live.
        </li>
        <li>
          <strong>Giữ chân</strong> ({formatNumber(rules.retainStar)} ⭐): đã đạt chuẩn; tới ngày{' '}
          {rules.retainAtDays} vẫn là Creator Studio đang hoạt động và từ ngày{' '}
          {rules.qualifyWindowDays + 1} đến {rules.retainAtDays} có ≥ {rules.retainLiveDays} ngày
          live hợp lệ.
        </li>
        <li>
          <strong>Nổi bật</strong> ({formatNumber(rules.standoutStar)} ⭐): trong{' '}
          {rules.standoutWindowDays} ngày đầu có ≥ {rules.standoutKcDays} ngày đạt mốc kim cương đầu
          tiên.
        </li>
        <li>
          Trần {formatNumber(rules.monthlyCap)} ⭐/tháng cho mỗi người tuyển — phần vượt không mất,
          trả vào tháng sau (mốc đạt trước trả trước). Sao đã trả không thu hồi.
        </li>
        <li>
          Thiếu dữ liệu cá nhân (vd. live chung kênh): Admin/SMOD <strong>xác nhận tay</strong>,
          bắt buộc ghi lý do, trong {rules.manualGraceDays} ngày sau hạn mốc. Người xác nhận không
          được là người tuyển.
        </li>
      </ul>
      {!fromServer && (
        <div style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
          Số liệu trên là mặc định; Admin chỉnh ở Điểm Sao → Cấu hình.
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageTitle
        title="Tuyển dụng (HR)"
        extra={
          <CustomButton
            bgColor="secondary-green"
            text="Thêm phiếu"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          />
        }
      />
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={policy} />
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Select
          allowClear
          placeholder="Mọi trạng thái"
          style={{ width: 170 }}
          value={filter.status}
          options={RECRUIT_STATUS_OPTIONS}
          onChange={(status) => setFilter((prev) => ({ ...prev, status, page: 1 }))}
        />
        <StarAccountSelect
          group="STAFF"
          placeholder="Người tuyển"
          allowClear
          style={{ width: 240 }}
          value={recruiter}
          onChange={(value) => {
            setRecruiter(value);
            setFilter((prev) => ({ ...prev, recruiterId: value?.value, page: 1 }));
          }}
        />
        <Input.Search
          allowClear
          placeholder="Tìm tên Creator / người tuyển"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!e.target.value) setFilter((prev) => ({ ...prev, q: undefined, page: 1 }));
          }}
          onSearch={(value) =>
            setFilter((prev) => ({ ...prev, q: value.trim() || undefined, page: 1 }))
          }
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={TABLE_EMPTY}
          scroll={{ x: 1120 }}
          expandable={{ expandedRowRender: expandedRow }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={filter.page}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <RecruitCreateModal
        open={createOpen}
        registerWindowDays={rules.registerWindowDays}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refresh()}
      />
      <ConfirmMilestoneModal
        recruit={confirmTarget}
        graceDays={rules.manualGraceDays}
        onClose={() => setConfirmTarget(null)}
        onDone={applyRow}
      />
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default StarRecruitsPage;
